/**
 * Always-valid confidence interval (confidence sequence) for the difference of
 * two proportions, via a Robbins normal-mixture martingale + Ville's inequality.
 *
 * A fixed-sample 95% CI (Wilson/Newcombe) guarantees `P(θ ∈ C_n) ≥ 1−α` for a
 * single pre-planned look only — peeking repeatedly and stopping "as soon as
 * it's green" inflates the false-positive rate well above α. A confidence
 * sequence instead guarantees
 *
 *     P(∀t : θ ∈ C_t) ≥ 1 − α
 *
 * — coverage holds at *every* sample size simultaneously, so continuous
 * monitoring and optional stopping (stop the moment the interval excludes zero)
 * are valid. The price is a wider interval than the fixed-sample read.
 *
 * Half-width (Robbins normal mixture) for the diff estimate θ̂ = pT − pC:
 *
 *     s   = sqrt( pT(1-pT)/nT + pC(1-pC)/nC )     // SE of the diff estimate
 *     V   = τ² / s²                                // information ÷ mixing variance
 *     w_t = s · sqrt( (V+1)/V · ( 2·ln(1/α) + ln(V+1) ) )
 *     C_t = θ̂ ± w_t
 *
 * This reduces to the one-sample form `w_t = s·sqrt((V+1)/V·(2ln(1/α)+ln(V+1)))`
 * with `V = t·τ²/σ²` (since `s² = σ²/t` ⇒ `τ²/s² = t·τ²/σ²`) and is two-sample
 * safe. τ is the prior SD on the effect; scaling it to the control baseline
 * (`τ = TAU_REL·pC`) keeps the sequence tightest around the relative lift we
 * care to estimate. τ tuning affects only width, never coverage; tying it to the
 * fast-converging control baseline (a nuisance parameter, not the effect itself)
 * is a standard, documented simplification.
 */

/** Two-sided error rate. 0.05 ⇒ 95% always-valid interval. */
export const ALWAYS_VALID_ALPHA = 0.05;

/**
 * Prior SD on the effect, as a fraction of the control baseline rate (~15%
 * relative lift). Tunes where the sequence is tightest; does not affect coverage.
 */
export const ALWAYS_VALID_TAU_REL = 0.15;

export interface AlwaysValidRateDiffInput {
    pTreatment: number | null | undefined;
    nTreatment: number | null | undefined;
    pControl: number | null | undefined;
    nControl: number | null | undefined;
    alpha?: number;
    tauRel?: number;
}

export interface AlwaysValidRateDiffResult {
    /** Point estimate of the difference, pTreatment − pControl. */
    diff: number;
    /** Lower always-valid bound, diff − halfWidth. */
    low: number;
    /** Upper always-valid bound, diff + halfWidth. */
    high: number;
    /** Mixture half-width w_t. */
    halfWidth: number;
}

/**
 * Always-valid CI on a two-proportion difference. Returns null when inputs are
 * unusable — an arm with no visitors, a zero standard error, or a zero control
 * baseline (relative lift, and the τ scaling, are both undefined there) — so
 * callers render "not enough data yet" rather than a misleading interval.
 */
export function alwaysValidRateDiffCi(
    input: AlwaysValidRateDiffInput
): AlwaysValidRateDiffResult | null {
    const pT = Number(input.pTreatment);
    const nT = Number(input.nTreatment);
    const pC = Number(input.pControl);
    const nC = Number(input.nControl);
    const alpha = input.alpha ?? ALWAYS_VALID_ALPHA;
    const tauRel = input.tauRel ?? ALWAYS_VALID_TAU_REL;

    if (
        !Number.isFinite(pT) ||
        !Number.isFinite(pC) ||
        !Number.isFinite(nT) ||
        !Number.isFinite(nC) ||
        nT <= 0 ||
        nC <= 0 ||
        pT < 0 ||
        pT > 1 ||
        pC < 0 ||
        pC > 1 ||
        !(alpha > 0 && alpha < 1) ||
        tauRel <= 0
    ) {
        return null;
    }

    const seSq = (pT * (1 - pT)) / nT + (pC * (1 - pC)) / nC;
    const tau = tauRel * pC;
    if (seSq <= 0 || tau <= 0) return null;

    const v = (tau * tau) / seSq;
    const s = Math.sqrt(seSq);
    const w = s * Math.sqrt(((v + 1) / v) * (2 * Math.log(1 / alpha) + Math.log(v + 1)));
    if (!Number.isFinite(w)) return null;

    const diff = pT - pC;
    return { diff, low: diff - w, high: diff + w, halfWidth: w };
}

/**
 * Standard-normal CDF Φ(z), via the Abramowitz–Stegun 7.1.26 erf approximation
 * (max abs error ≈ 1.5e-7). The repo only carries this in SQL
 * (`analytics_helpers.experiment_normal_cdf`); this is its TS twin.
 */
export function standardNormalCdf(z: number): number {
    if (!Number.isFinite(z)) return Number.NaN;
    const sign = z < 0 ? -1 : 1;
    const x = Math.abs(z) / Math.SQRT2;
    const t = 1 / (1 + 0.3275911 * x);
    const y =
        1 -
        ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t +
            0.254829592) *
            t *
            Math.exp(-x * x);
    return 0.5 * (1 + sign * y);
}

export interface BayesianRateDiffInput {
    cTreatment: number | null | undefined;
    nTreatment: number | null | undefined;
    cControl: number | null | undefined;
    nControl: number | null | undefined;
}

export interface BayesianRateDiffResult {
    /** Posterior probability the treatment rate exceeds the control rate. */
    probPositive: number;
    /** Posterior mean of the rate difference (regularised toward zero). */
    posteriorMeanDiff: number;
    /** Posterior SD of the rate difference. */
    sd: number;
    /** Lower 95% credible bound on the difference. */
    credLow: number;
    /** Upper 95% credible bound on the difference. */
    credHigh: number;
}

/**
 * Bayesian posterior on a two-proportion difference under a per-arm
 * Beta-Binomial model with the Jeffreys prior `Beta(0.5, 0.5)`.
 *
 * Unlike a normal/Wald approximation, this is well-behaved at low conversion
 * rates and stays defined when an arm has zero conversions (the prior keeps the
 * posterior mean and variance positive). Per arm with `c` conversions of `n`
 * visitors the posterior is `Beta(c+0.5, n-c+0.5)`, with
 *
 *     μ   = (c + 0.5) / (n + 1)
 *     var = μ(1-μ) / (n + 2)
 *
 * The difference of the two posteriors is summarised with a normal approximation
 * on the *difference of means* (far better-behaved than approximating each
 * proportion's tail):
 *
 *     Δ  = μT − μC          // de-biased best estimate (regularised toward 0)
 *     sd = sqrt(varT + varC)
 *     P(treatment wins) = Φ(Δ / sd)
 *     95% credible interval = Δ ± 1.96·sd
 *
 * Returns null only when an arm has no visitors. Conversion counts may be
 * fractional (callers reconstructing `c = rate·n` need not round).
 */
export function bayesianRateDiffPosterior(
    input: BayesianRateDiffInput
): BayesianRateDiffResult | null {
    const cT = Number(input.cTreatment);
    const nT = Number(input.nTreatment);
    const cC = Number(input.cControl);
    const nC = Number(input.nControl);

    if (
        !Number.isFinite(cT) ||
        !Number.isFinite(nT) ||
        !Number.isFinite(cC) ||
        !Number.isFinite(nC) ||
        nT <= 0 ||
        nC <= 0 ||
        cT < 0 ||
        cC < 0 ||
        cT > nT ||
        cC > nC
    ) {
        return null;
    }

    const muT = (cT + 0.5) / (nT + 1);
    const muC = (cC + 0.5) / (nC + 1);
    const varT = (muT * (1 - muT)) / (nT + 2);
    const varC = (muC * (1 - muC)) / (nC + 2);
    const sd = Math.sqrt(varT + varC);
    if (!(sd > 0)) return null;

    const diff = muT - muC;
    const probPositive = standardNormalCdf(diff / sd);
    return {
        probPositive,
        posteriorMeanDiff: diff,
        sd,
        credLow: diff - 1.96 * sd,
        credHigh: diff + 1.96 * sd,
    };
}
